/**
 * ChannelTreeNode - Custom node renderer for react-arborist tree.
 * Right-click context menu for creating sub-channels.
 */

import React, { CSSProperties, useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface ChannelTreeNodeProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  node: any;
  style: CSSProperties;
  dragHandle?: ((el: HTMLDivElement | null) => void);
  unreadCount: number;
  mentionCount: number;
  onCreateSubChannel?: (parentId: string) => void;
}

const ChannelTreeNode: React.FC<ChannelTreeNodeProps> = ({
  node,
  style,
  dragHandle,
  unreadCount,
  mentionCount,
  onCreateSubChannel,
}) => {
  const { t } = useTranslation('chat');
  const data = node.data?.data || {};
  const isSelected = node.isSelected;
  const [showMenu, setShowMenu] = useState(false);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  const getIcon = (): string => {
    if (data.icon) return data.icon;
    switch (data.channel_type) {
      case 'direct_message': return '@';
      case 'private': return '~';
      case 'announcement': return '!';
      default: return '#';
    }
  };

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    // Only show context menu for non-DM channels
    if (data.channel_type === 'direct_message') return;
    e.preventDefault();
    e.stopPropagation();
    setMenuPos({ x: e.clientX, y: e.clientY });
    setShowMenu(true);
  }, [data.channel_type]);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);

  return (
    <>
      <div
        ref={dragHandle}
        style={style}
        className={`flex items-center px-3 py-1 cursor-pointer text-sm rounded mx-1 group ${
          isSelected
            ? 'bg-blue-600 text-white'
            : 'text-gray-300 hover:bg-gray-800'
        } ${data.is_archived ? 'opacity-60' : ''}`}
        onClick={() => node.isInternal && node.toggle()}
        onContextMenu={handleContextMenu}
      >
        {node.isInternal && (
          <span className="mr-1 text-xs text-gray-500">
            {node.isOpen ? '\u25BC' : '\u25B6'}
          </span>
        )}
        <span className="mr-2 text-gray-500 text-xs font-mono">{getIcon()}</span>
        <span className={`flex-1 truncate ${unreadCount > 0 ? 'font-semibold text-white' : ''}`}>
          {node.data.name}
        </span>
        {mentionCount > 0 && (
          <span className="ml-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
            {mentionCount}
          </span>
        )}
        {unreadCount > 0 && mentionCount === 0 && (
          <span className="ml-1 w-2 h-2 bg-blue-400 rounded-full" />
        )}
      </div>

      {/* Context menu - portalled to body to avoid transform offset from react-arborist */}
      {showMenu && createPortal(
        <div
          ref={menuRef}
          className="fixed bg-white text-gray-900 rounded shadow-lg border py-1 z-50 min-w-[160px]"
          style={{ left: menuPos.x, top: menuPos.y }}
        >
          <button
            className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100"
            onClick={() => {
              setShowMenu(false);
              onCreateSubChannel?.(data.id);
            }}
          >
            {t('createSubChannel')}
          </button>
        </div>,
        document.body
      )}
    </>
  );
};

export default ChannelTreeNode;
